import { motion } from 'framer-motion';
import { CapacityItem } from 'shared/components/charts/types';
import { GROW, STAGGER } from 'shared/components/motion/tokens';
import { useRevealOnce } from 'shared/components/motion/useRevealOnce';
import { CHART_INK, STATUS_COLORS } from 'shared/constants/chartPalette';

interface CapacityBarListProps {
  items: CapacityItem[];
  /** Como mostrar "valor / capacidade" ao lado do percentual (ex: "7/8 alunos", "181 h de 216 h"). */
  formatRatio?: (item: CapacityItem) => string;
  /** Acima deste % a barra ganha a cor de "lotado" — sinal para abrir turma/horário extra. */
  fullAt?: number;
  /** Abaixo deste % a barra ganha a cor de alerta — capacidade ociosa. */
  lowAt?: number;
}

const defaultFormatRatio = (item: CapacityItem) => `${item.value}/${item.capacity}`;

export function fillPct(item: CapacityItem) {
  if (item.pct !== undefined) return item.pct;
  return item.capacity > 0 ? (item.value / item.capacity) * 100 : 0;
}

export function CapacityBarList({ items, formatRatio = defaultFormatRatio, fullAt = 95, lowAt = 55 }: CapacityBarListProps) {
  const [ref, inView] = useRevealOnce();

  return (
    <div ref={ref} className="flex flex-col gap-3">
      {items.map((item, index) => {
        const pct = fillPct(item);
        const color = pct >= fullAt ? STATUS_COLORS.good : pct < lowAt ? STATUS_COLORS.warning : CHART_INK.brand;

        return (
          <div key={item.id}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-ink">{item.label}</span>
              <span className="flex-shrink-0 font-medium text-ink">
                {pct.toFixed(0)}%
                <span className="ml-2 text-xs font-normal text-ink-secondary">{formatRatio(item)}</span>
              </span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-chip">
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${color}bb, ${color})` }}
                initial={{ width: 0 }}
                animate={{ width: inView ? `${Math.min(100, pct)}%` : 0 }}
                transition={{ ...GROW, delay: inView ? index * STAGGER : 0 }}
              />
            </div>
            {item.note && <p className="mt-1 text-xs text-ink-secondary">{item.note}</p>}
          </div>
        );
      })}

      <div className="mt-1 flex flex-wrap gap-4 text-xs text-ink-secondary">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.good }} /> Lotado (≥ {fullAt}%)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_INK.brand }} /> Saudável
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.warning }} /> Ociosa (&lt; {lowAt}%)
        </span>
      </div>
    </div>
  );
}
